/**
 * Labstep
 */

import { SLASH_TRIGGER } from 'labstep-web/constants/prosemirror';
import type {
  IStateDispatchProps,
  ProseMirrorCommand,
} from 'labstep-web/prosemirror/marks/types';
import { replaceWithNode } from 'labstep-web/prosemirror/nodes/commands';
import { getToken } from 'labstep-web/prosemirror/utils/selection';
import bugsnagService from 'labstep-web/services/bugsnag.service';
import type {
  NodeType,
  Node as ProsemirrorNode,
  Schema,
} from 'prosemirror-model';
import { liftListItem, wrapInList } from 'prosemirror-schema-list';
import type { EditorState } from 'prosemirror-state';
import { findParentNode } from 'prosemirror-utils';

function isList(node: ProsemirrorNode, schema: Schema) {
  return (
    node.type === schema.nodes.bullet_list ||
    node.type === schema.nodes.ordered_list
  );
}

type TypeToggleList = (
  listType: NodeType,
  itemType: NodeType,
) => ProseMirrorCommand;

export const toggleList: TypeToggleList = (
  listType: NodeType,
  itemType: NodeType,
) => {
  return (state, dispatch) => {
    try {
      const { schema, selection } = state;
      const { $from, $to } = selection;
      const range = $from.blockRange($to);

      if (!range) {
        return false;
      }

      const parentList = findParentNode((node) =>
        isList(node, schema),
      )(selection);

      if (
        range.depth >= 1 &&
        parentList &&
        range.depth - parentList.depth <= 1
      ) {
        if (parentList.node.type === listType) {
          return liftListItem(itemType)(state, dispatch);
        }

        if (
          isList(parentList.node, schema) &&
          listType.validContent(parentList.node.content)
        ) {
          const { tr } = state;

          tr.setNodeMarkup(parentList.pos, listType);

          if (dispatch) {
            dispatch(tr);
          }

          return true;
        }
      }

      return wrapInList(listType)(state, dispatch);
    } catch (e) {
      bugsnagService.notify(e);
      return false;
    }
  };
};

/**
 * Creates a list from slash command
 * @param listType - The type of list to create ('bullet_list' or 'ordered_list')
 */
export function insertList(listType: 'bullet_list' | 'ordered_list') {
  return (
    state: EditorState,
    dispatch: IStateDispatchProps['dispatch'],
  ) => {
    const token = getToken(state, SLASH_TRIGGER);
    if (!token) {
      return;
    }

    const { from, to } = token;

    // Create a list item with an empty paragraph
    const listItem = state.schema.nodes.list_item.createAndFill();
    if (!listItem) {
      return;
    }

    replaceWithNode(
      state,
      dispatch,
      from,
      to,
      listType,
      {},
      listItem,
    );
  };
}
